/**
 * Software-design reference shape manifests.
 *
 * One reference shape per domain (11 total). These are the scaffold
 * templates — Epic B fills in the full ~65 shapes by copying the pattern
 * for the remaining constructs (columns, enums, methods, fragments, etc.)
 * per domain.
 *
 * Rendering is intentionally minimal — a rounded-rect with a colored
 * accent stripe and a label, same pattern as network basic-shapes. Epic
 * B replaces the SVG renderer with domain-specific visuals (three-compartment
 * class boxes, sequence lifelines, state rounded rectangles, C4 system
 * boundaries, ADR cards with status badges, etc.).
 *
 * See plans/software-design/foundation-scaffold.md § Layer 4 and
 * plans/software-design/domain.md for the full shape list.
 */

import { z } from 'zod';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';

const softwareDesignNodeProps = z.object({
  name: z.string().default(''),
  domain: z.string().default('class'), // one of the 11 domain types
  stereotype: z.string().optional(),
  fillColor: z.string().default('#FFFFFF'),
  strokeColor: z.string().default('#94a3b8'),
  textColor: z.string().default('#1e293b'),
  accentColor: z.string().default('#6366f1'),
});

export type SoftwareDesignNodeProps = z.infer<typeof softwareDesignNodeProps>;

function renderSoftwareDesignNodeSvg(
  viewModel: { width?: number; height?: number },
  props: Record<string, unknown>,
): string {
  const w = viewModel.width ?? 220;
  const h = viewModel.height ?? 80;
  const name = String(props.name || 'Node');
  const domain = String(props.domain || '');
  const stereotype = String(props.stereotype || '');
  const accent = String(props.accentColor || '#6366f1');
  const fill = String(props.fillColor || '#FFFFFF');
  const stroke = String(props.strokeColor || '#94a3b8');
  const textColor = String(props.textColor || '#1e293b');

  const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const parts: string[] = [
    `<rect x="0" y="0" width="${w}" height="${h}" rx="6" ry="6" fill="${fill}" stroke="${stroke}" stroke-width="1.5" />`,
    `<rect x="0" y="0" width="4" height="${h}" rx="2" ry="2" fill="${accent}" />`,
  ];
  if (stereotype) {
    parts.push(
      `<text x="14" y="18" font-size="10" font-style="italic" fill="#64748b" font-family="Arial, sans-serif">«${escape(stereotype)}»</text>`,
    );
  }
  parts.push(
    `<text x="14" y="${Math.round(h / 2 + 4)}" font-size="13" font-weight="600" fill="${textColor}" font-family="Arial, sans-serif">${escape(name)}</text>`,
    `<text x="14" y="${h - 10}" font-size="9" fill="#64748b" font-family="Arial, sans-serif">${escape(domain.toUpperCase())}</text>`,
  );
  return parts.join('');
}

interface SoftwareDesignManifestSpec {
  id: string;
  domain: string;
  label: string;
  accent: string;
  icon?: string;
}

function createSoftwareDesignManifest(spec: SoftwareDesignManifestSpec): ShapeManifest<SoftwareDesignNodeProps> {
  return {
    id: spec.id,
    version: '1.0.0',
    category: 'software-design',
    icon: spec.icon ?? 'box',
    propsSchema: softwareDesignNodeProps,
    defaultProps: () => ({
      name: spec.label,
      domain: spec.domain,
      fillColor: '#FFFFFF',
      strokeColor: '#94a3b8',
      textColor: '#1e293b',
      accentColor: spec.accent,
    }),
    defaultDimensions: { width: 220, height: 80 },
    anchors: {
      snap: ['top', 'right', 'bottom', 'left', 'center'],
      perimeter: 'rect',
    },
    capabilities: {
      selectable: true,
      transformable: true,
      textEditable: true,
      connectable: true,
    },
    properties: [
      { key: 'name', label: 'Name', kind: 'string', required: true, ui: { group: 'identity', priority: 1 } },
      { key: 'stereotype', label: 'Stereotype', kind: 'string', ui: { group: 'identity', priority: 2 } },
    ],
    acl: () => true,
    renderers: {
      svg: (args: { viewModel: unknown; props: unknown }) =>
        renderSoftwareDesignNodeSvg(
          args.viewModel as { width?: number; height?: number },
          args.props as Record<string, unknown>,
        ),
    },
  };
}

// ── Reference manifests — one per domain ─────────────────────────────
// Epic B replaces each with domain-specific Konva + SVG renderers and
// adds the remaining shapes (columns, methods, lifelines, etc.).

export const ErdEntityManifest = createSoftwareDesignManifest({
  id: 'erd_entity',
  domain: 'erd',
  label: 'Entity',
  accent: '#3b82f6', // blue
  icon: 'table',
});

export const DbTableManifest = createSoftwareDesignManifest({
  id: 'db_table',
  domain: 'database',
  label: 'Table',
  accent: '#2563eb',
  icon: 'table',
});

export const ClassClassManifest = createSoftwareDesignManifest({
  id: 'class_class',
  domain: 'class',
  label: 'Class',
  accent: '#8b5cf6', // purple
  icon: 'box',
});

export const SeqParticipantManifest = createSoftwareDesignManifest({
  id: 'seq_participant',
  domain: 'sequence',
  label: 'Participant',
  accent: '#a855f7',
  icon: 'user',
});

export const CompComponentManifest = createSoftwareDesignManifest({
  id: 'comp_component',
  domain: 'component',
  label: 'Component',
  accent: '#6366f1',
  icon: 'box',
});

export const UcUseCaseManifest = createSoftwareDesignManifest({
  id: 'uc_usecase',
  domain: 'usecase',
  label: 'Use Case',
  accent: '#14b8a6',
  icon: 'circle',
});

export const StateStateManifest = createSoftwareDesignManifest({
  id: 'state_state',
  domain: 'state',
  label: 'State',
  accent: '#10b981',
  icon: 'box',
});

export const ActActionManifest = createSoftwareDesignManifest({
  id: 'act_action',
  domain: 'activity',
  label: 'Action',
  accent: '#22c55e',
  icon: 'box',
});

export const DfdProcessManifest = createSoftwareDesignManifest({
  id: 'dfd_process',
  domain: 'dfd',
  label: 'Process',
  accent: '#f59e0b',
  icon: 'circle',
});

export const C4ContainerManifest = createSoftwareDesignManifest({
  id: 'c4_container',
  domain: 'c4',
  label: 'Container',
  accent: '#06b6d4',
  icon: 'box',
});

export const AdrDecisionManifest = createSoftwareDesignManifest({
  id: 'adr_decision',
  domain: 'adr',
  label: 'Decision',
  accent: '#ec4899',
  icon: 'file',
});

export const SoftwareDesignShapeManifests = [
  ErdEntityManifest,
  DbTableManifest,
  ClassClassManifest,
  SeqParticipantManifest,
  CompComponentManifest,
  UcUseCaseManifest,
  StateStateManifest,
  ActActionManifest,
  DfdProcessManifest,
  C4ContainerManifest,
  AdrDecisionManifest,
];
